/**
 * Pure Tennis ITF transition helpers (deterministic state machine).
 * Official ITF Rules of Tennis — Point → Game → Set → Match.
 *
 * Persist: score · server · receiver · serveCourt · orders · ends.
 * Derive: display points (0/15/30/40/Ad) · L/R court occupants (ADR-033).
 */

/** @typedef {'A'|'B'} Side */
/** @typedef {'right'|'left'} ServeCourt */

export const DEFAULT_TENNIS_RULES = Object.freeze({
  gamesToWin: 6,
  winByGames: 2,
  tiebreakAt: 6,
  tiebreakTo: 7,
  noAd: false,
  /** When true, deciding set is a 10-point Match Tiebreak (no games race). */
  matchTiebreak: false,
  matchTiebreakTo: 10,
});

/**
 * Persist points as 0/1/2/3…; UI derives Love/15/30/40/Ad.
 * @param {number} points
 * @param {number} opp
 */
export function pointCall(points, opp) {
  if (points >= 3 && opp >= 3) {
    if (points === opp) return "40";
    if (points === opp + 1) return "Ad";
    return "40";
  }
  return ["0", "15", "30", "40"][Math.min(points, 3)] || "40";
}

/** @param {Side} side */
export function otherSide(side) {
  return side === "A" ? "B" : "A";
}

/** @param {ServeCourt} court */
export function toggleCourt(court) {
  return court === "right" ? "left" : "right";
}

/**
 * Service court from points already played in the game (or TB).
 * Even → Deuce (right); odd → Ad (left).
 * @param {number} pointsPlayedInGame
 * @returns {ServeCourt}
 */
export function serveCourtForPointsPlayed(pointsPlayedInGame) {
  return Number(pointsPlayedInGame) % 2 === 0 ? "right" : "left";
}

/**
 * Ends change after odd-numbered completed games in the set (1, 3, 5…).
 * @param {number} gameSum gamesA + gamesB after the completed game
 */
export function shouldChangeEndsAfterGame(gameSum) {
  return Number(gameSum) % 2 === 1;
}

/**
 * ITF: change ends at end of set unless total games in that set is even.
 * @param {number} gamesA
 * @param {number} gamesB
 */
export function shouldChangeEndsAfterSet(gamesA, gamesB) {
  return (Number(gamesA) + Number(gamesB)) % 2 === 1;
}

/**
 * Tiebreak / Match TB: change ends every 6 points.
 * @param {number} pointsPlayed cumulative TB points after the point
 */
export function shouldChangeEndsInTiebreak(pointsPlayed) {
  return Number(pointsPlayed) > 0 && Number(pointsPlayed) % 6 === 0;
}

/**
 * TB serve: first point one serve, then change every two points.
 * @param {number} tbServeCount points completed in TB (after awarding point)
 */
export function shouldChangeTbServer(tbServeCount) {
  const count = Number(tbServeCount);
  return count === 1 || (count > 1 && (count - 1) % 2 === 0);
}

/**
 * @param {number} pa
 * @param {number} pb
 * @param {boolean} [noAd]
 * @returns {Side|null}
 */
export function gameWinnerFromPoints(pa, pb, noAd = false) {
  if (noAd && pa >= 3 && pb >= 3 && pa !== pb) {
    return pa > pb ? "A" : "B";
  }
  if (pa >= 4 && pa - pb >= 2) return "A";
  if (pb >= 4 && pb - pa >= 2) return "B";
  return null;
}

/**
 * @param {number} a
 * @param {number} b
 * @param {{ gamesToWin?: number, winByGames?: number }} rules
 * @returns {Side|null}
 */
export function setWinnerFromGames(a, b, rules) {
  const to = rules?.gamesToWin ?? 6;
  const by = rules?.winByGames ?? 2;
  if (a >= to && a - b >= by) return "A";
  if (b >= to && b - a >= by) return "B";
  return null;
}

/**
 * @param {number} a
 * @param {number} b
 * @param {number} [to]
 * @returns {Side|null}
 */
export function tiebreakWinner(a, b, to = 7) {
  const target = Number(to) || 7;
  if ((a >= target || b >= target) && Math.abs(a - b) >= 2) {
    return a > b ? "A" : "B";
  }
  return null;
}

/**
 * Both sides need one more set to win the match.
 * @param {object} match
 */
export function isDecidingSet(match) {
  const need = Math.ceil((match?.bestOf || 3) / 2);
  const sets = match?.sets || [];
  const wonA = sets.filter((s) => (s.setWinner || s.winner) === "A").length;
  const wonB = sets.filter((s) => (s.setWinner || s.winner) === "B").length;
  return wonA === need - 1 && wonB === need - 1;
}

/**
 * Fixed doubles serve rotation for a set: A1 → B1 → A2 → B2 → …
 * @param {{ serveFirstIndexA?: number, serveFirstIndexB?: number }} match
 */
export function serveRotation(match) {
  const aFirst = match.serveFirstIndexA ?? 0;
  const bFirst = match.serveFirstIndexB ?? 0;
  const partner = (idx) => (idx === 0 ? 1 : 0);
  return [
    { side: "A", playerIndex: aFirst },
    { side: "B", playerIndex: bFirst },
    { side: "A", playerIndex: partner(aFirst) },
    { side: "B", playerIndex: partner(bFirst) },
  ];
}

export function partnerIndex(idx) {
  return idx === 0 ? 1 : 0;
}

/**
 * Derive doubles court slots (right=deuce, left=ad) from server + receive order.
 * Never persist L/R as player properties (ADR-033).
 *
 * @param {object} args
 * @param {'singles'|'doubles'} args.mode
 * @param {ServeCourt} args.serveCourt
 * @param {Side} args.servingSide
 * @param {string|null} args.serverId
 * @param {Array<{id: string}>} args.sideAPlayers
 * @param {Array<{id: string}>} args.sideBPlayers
 * @param {number} [args.receiveDeuceIndexA]
 * @param {number} [args.receiveDeuceIndexB]
 */
export function deriveTennisCourtLayout({
  mode,
  serveCourt,
  servingSide,
  serverId,
  sideAPlayers = [],
  sideBPlayers = [],
  receiveDeuceIndexA = 0,
  receiveDeuceIndexB = 0,
}) {
  if (mode !== "doubles") {
    return {
      sideA: {
        rightPlayerId: sideAPlayers[0]?.id || null,
        leftPlayerId: null,
      },
      sideB: {
        rightPlayerId: sideBPlayers[0]?.id || null,
        leftPlayerId: null,
      },
      serveCourt: serveCourt || "right",
    };
  }

  const deuceIdx = (side) =>
    side === "A" ? receiveDeuceIndexA ?? 0 : receiveDeuceIndexB ?? 0;

  const fixedReceive = (players, side) => {
    if (!players.length) {
      return { rightPlayerId: null, leftPlayerId: null };
    }
    const d = Math.max(0, Math.min(deuceIdx(side), players.length - 1));
    const ad = partnerIndex(d);
    return {
      rightPlayerId: players[d]?.id || null,
      leftPlayerId: players[ad]?.id || players[d]?.id || null,
    };
  };

  const servingFormation = (players) => {
    if (!players.length) {
      return { rightPlayerId: null, leftPlayerId: null };
    }
    const partner =
      players.find((p) => p.id !== serverId) || players[1] || players[0];
    const rightId = serveCourt === "right" ? serverId : partner?.id;
    const leftId = serveCourt === "left" ? serverId : partner?.id;
    return {
      rightPlayerId: rightId || null,
      leftPlayerId: leftId || null,
    };
  };

  if (servingSide === "A") {
    return {
      sideA: servingFormation(sideAPlayers),
      sideB: fixedReceive(sideBPlayers, "B"),
      serveCourt: serveCourt || "right",
    };
  }
  return {
    sideA: fixedReceive(sideAPlayers, "A"),
    sideB: servingFormation(sideBPlayers),
    serveCourt: serveCourt || "right",
  };
}

/**
 * Whether the next point wins the tiebreak for `side`.
 * @param {number} lead
 * @param {number} trail
 * @param {number} to
 */
export function nextPointWinsTiebreak(lead, trail, to) {
  return lead + 1 >= Number(to) && lead + 1 - trail >= 2;
}
